import React from 'react'
import { createPortal } from 'react-dom'
import { addMinutes, format, isValid } from 'date-fns'
import { getCalendarEventDisplayName } from './calendarDisplayUtils'

const TOOLTIP_DELAY_MS = 750
const VIEWPORT_GUTTER = 12
const TOOLTIP_GAP = 8
let tooltipId = 0

const capitalize = (value) => {
  const text = String(value || '')
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : ''
}

const getEventDateTimeLabel = (event) => {
  const start = new Date(event.dateTime)
  const lessonLength = Number(event.lessonLength || 0)

  if (!isValid(start)) {
    return [event.time, lessonLength ? `${lessonLength} min` : '']
      .filter(Boolean)
      .join(' · ')
  }

  const end = addMinutes(start, lessonLength)
  const timeRange = lessonLength > 0
    ? `${format(start, 'h:mm a')}–${format(end, 'h:mm a')}`
    : format(start, 'h:mm a')

  return `${format(start, 'EEEE, MMMM d')} · ${timeRange}`
}

const getEventStatuses = (event) => [
  event.isIntro && 'Intro lesson',
  event.isMakeupLesson && 'Make-up lesson',
  event.isPendingOffer && 'Pending offer',
  event.recurring?.isOn && event.recurring?.frequency !== 'none'
    && `${capitalize(event.recurring.frequency)} recurring`,
].filter(Boolean)

const CalendarEventHoverTooltip = ({ event, children }) => {
  const anchorRef = React.useRef(null)
  const tooltipRef = React.useRef(null)
  const timerRef = React.useRef(null)
  const tooltipIdRef = React.useRef(null)
  const [isVisible, setIsVisible] = React.useState(false)
  const [position, setPosition] = React.useState({ left: 0, top: 0, ready: false })

  if (!tooltipIdRef.current) {
    tooltipId += 1
    tooltipIdRef.current = `calendar-event-tooltip-${tooltipId}`
  }

  const clearTimer = React.useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const scheduleTooltip = React.useCallback(() => {
    clearTimer()
    timerRef.current = window.setTimeout(() => {
      setIsVisible(true)
      timerRef.current = null
    }, TOOLTIP_DELAY_MS)
  }, [clearTimer])

  const hideTooltip = React.useCallback(() => {
    clearTimer()
    setIsVisible(false)
    setPosition((current) => ({ ...current, ready: false }))
  }, [clearTimer])

  const updatePosition = React.useCallback(() => {
    if (!anchorRef.current || !tooltipRef.current) return

    const anchor = anchorRef.current.getBoundingClientRect()
    const tooltip = tooltipRef.current.getBoundingClientRect()
    let left = anchor.left
    let top = anchor.bottom + TOOLTIP_GAP

    if (left + tooltip.width > window.innerWidth - VIEWPORT_GUTTER) {
      left = window.innerWidth - tooltip.width - VIEWPORT_GUTTER
    }
    if (top + tooltip.height > window.innerHeight - VIEWPORT_GUTTER) {
      top = anchor.top - tooltip.height - TOOLTIP_GAP
    }

    setPosition({
      left: Math.max(VIEWPORT_GUTTER, left),
      top: Math.max(VIEWPORT_GUTTER, top),
      ready: true,
    })
  }, [])

  React.useLayoutEffect(() => {
    if (!isVisible) return undefined

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isVisible, updatePosition])

  React.useEffect(() => () => clearTimer(), [clearTimer])

  const child = React.Children.only(children)
  const trigger = React.cloneElement(child, {
    ref: anchorRef,
    tabIndex: child.props.tabIndex ?? 0,
    'aria-describedby': isVisible ? tooltipIdRef.current : child.props['aria-describedby'],
    onMouseEnter: (mouseEvent) => {
      child.props.onMouseEnter?.(mouseEvent)
      scheduleTooltip()
    },
    onMouseLeave: (mouseEvent) => {
      child.props.onMouseLeave?.(mouseEvent)
      hideTooltip()
    },
    onFocus: (focusEvent) => {
      child.props.onFocus?.(focusEvent)
      scheduleTooltip()
    },
    onBlur: (focusEvent) => {
      child.props.onBlur?.(focusEvent)
      hideTooltip()
    },
    onClick: (mouseEvent) => {
      hideTooltip()
      child.props.onClick?.(mouseEvent)
    },
    onKeyDown: (keyboardEvent) => {
      child.props.onKeyDown?.(keyboardEvent)
      if (keyboardEvent.key === 'Escape') hideTooltip()
    },
  })

  const statuses = getEventStatuses(event)
  const displayName = getCalendarEventDisplayName(event.name)
  const teacherName = String(event.teacherName || '').trim()
  const studentName = String(event.studentName || '').trim()
  const familyName = String(event.familyName || '').trim()
  const colorClass = event.color?.bgColor500 || 'bg-blue-500'

  const tooltip = isVisible && typeof document !== 'undefined'
    ? createPortal(
        <div
          ref={tooltipRef}
          id={tooltipIdRef.current}
          role='tooltip'
          className='calendar__event-tooltip'
          style={{
            left: `${position.left}px`,
            top: `${position.top}px`,
            visibility: position.ready ? 'visible' : 'hidden',
          }}
        >
          <div className='calendar__event-tooltip-heading'>
            <span className={`calendar__event-tooltip-color ${colorClass}`} aria-hidden='true' />
            <strong>{displayName}</strong>
            {Number(event.lessonLength) > 0 && (
              <span className='calendar__event-tooltip-length'>{event.lessonLength} min</span>
            )}
          </div>
          <p className='calendar__event-tooltip-time'>{getEventDateTimeLabel(event)}</p>
          {(teacherName || studentName || familyName) && (
            <dl className='calendar__event-tooltip-meta'>
              {teacherName && (
                <div><dt>Teacher</dt><dd>{teacherName}</dd></div>
              )}
              {studentName && studentName !== displayName && (
                <div><dt>Student</dt><dd>{studentName}</dd></div>
              )}
              {familyName && (
                <div><dt>Family</dt><dd>{familyName}</dd></div>
              )}
            </dl>
          )}
          {statuses.length > 0 && (
            <div className='calendar__event-tooltip-statuses'>
              {statuses.map((status) => <span key={status}>{status}</span>)}
            </div>
          )}
        </div>,
        document.body
      )
    : null

  return <>{trigger}{tooltip}</>
}

export default CalendarEventHoverTooltip
